import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

const AreasPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'Areas We Cover | Removals Across Lancashire | Dan & Van',
    description: 'Professional removals across Lancashire: Accrington, Blackburn, Burnley, Preston, Lancaster, Chorley & more. Local knowledge, reliable service — 07530 166035',
    keywords: 'removals Accrington, removals Blackburn, removals Burnley, removals Preston, removals Lancaster, Lancashire movers',
    canonical: '/areas-we-cover',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Areas We Cover',
      description: 'Dan & Van Removals proudly serves Lancashire and surrounding regions with professional moving services.',
      provider: {
        '@type': 'MovingCompany',
        name: 'Dan & Van Removals',
        telephone: '07530166035',
        areaServed: [
          'Accrington',
          'Clayton-le-Moors',
          'Blackburn',
          'Burnley',
          'Preston',
          'Lancaster',
          'Chorley',
          'Darwen',
          'Clitheroe',
          'Rawtenstall',
          'Nelson',
          'Colne',
          'Leyland',
          'Ormskirk',
          'Skelmersdale',
        ],
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const areas = [
    {
      name: 'Accrington',
      description: 'Professional house and office removals in Accrington. We know the local area well and provide reliable moving services throughout the town and surrounding villages.',
    },
    {
      name: 'Clayton-le-Moors',
      description: 'Trusted removals service in Clayton-le-Moors. From single item moves to complete house relocations, we handle every move with care and professionalism.',
    },
    {
      name: 'Blackburn',
      description: 'Expert removals across Blackburn. Whether you\'re moving within the town or relocating to a new area, our experienced team ensures a smooth, stress-free move.',
    },
    {
      name: 'Burnley',
      description: 'Reliable moving services in Burnley. We provide fully insured removals for homes and businesses, with flexible scheduling to suit your needs.',
    },
    {
      name: 'Preston',
      description: 'Premium removals in Preston. Our professional team handles everything from packing to transportation, ensuring your belongings arrive safely at your new location.',
    },
    {
      name: 'Lancaster',
      description: 'Comprehensive moving services in Lancaster. We specialise in house removals, office relocations, and single item transportation throughout the city.',
    },
    {
      name: 'Chorley',
      description: 'Trusted removals service in Chorley. With years of local experience, we provide efficient, careful moving services for residential and commercial clients.',
    },
    {
      name: 'Darwen',
      description: 'Professional removals in Darwen. From small flats to large family homes, we handle moves of all sizes with the same level of care and attention.',
    },
    {
      name: 'Clitheroe',
      description: 'Expert moving services in Clitheroe. We understand the unique challenges of moving in the Ribble Valley and provide tailored solutions for every client.',
    },
    {
      name: 'Rawtenstall',
      description: 'Reliable removals in Rawtenstall and the Rossendale Valley. Our team navigates local roads with ease, ensuring timely and safe delivery of your belongings.',
    },
    {
      name: 'Nelson',
      description: 'Professional moving services in Nelson. We offer competitive rates and flexible scheduling for house removals, office moves, and single item transportation.',
    },
    {
      name: 'Colne',
      description: 'Trusted removals service in Colne. Our experienced team provides fully insured moving services with protective equipment to keep your items safe.',
    },
    {
      name: 'Leyland',
      description: 'Expert removals in Leyland. Whether you\'re moving locally or further afield, we provide comprehensive moving services tailored to your requirements.',
    },
    {
      name: 'Ormskirk',
      description: 'Professional moving services in Ormskirk. We handle residential and commercial moves with care, efficiency, and competitive pricing.',
    },
    {
      name: 'Skelmersdale',
      description: 'Reliable removals in Skelmersdale. From packing to unloading, our team manages every aspect of your move with professionalism and attention to detail.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c4fd92cefe9cae17053599e3e0e8dfe8.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-white/80"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-sans text-6xl font-bold text-charcoal mb-6"
          >
            Areas We Cover
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-700"
          >
            Professional removals across Lancashire and surrounding areas
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Dan & Van Removals proudly serves Lancashire and the surrounding regions. With extensive local knowledge and years of experience, we provide reliable, professional moving services throughout the area. Below are some of the key locations we cover.
            </p>
          </div>

          {/* Areas Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {areas.map((area, index) => (
              <motion.div
                key={area.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gold hover:shadow-luxury transition-all"
              >
                <h2 className="font-sans text-2xl font-bold text-charcoal mb-3">
                  Removals in {area.name}
                </h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {area.description}
                </p>
                <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                  <Link to="/house-removals" className="hover:text-gold transition-colors cursor-pointer">
                    House Removals
                  </Link>
                  <span>|</span>
                  <Link to="/office-removals" className="hover:text-gold transition-colors cursor-pointer">
                    Office Removals
                  </Link>
                  <span>|</span>
                  <Link to="/single-item-moves" className="hover:text-gold transition-colors cursor-pointer">
                    Single Items
                  </Link>
                </div>
                <div className="mt-4 pt-4 border-t border-gold/20">
                  <Link
                    to="/contact"
                    className="text-gold font-medium hover:underline flex items-center cursor-pointer"
                  >
                    Get a quote for {area.name} removals
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="bg-gray-50 rounded-lg p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6 text-center">
                Don't see your area listed?
              </h3>
              <p className="text-lg text-gray-700 mb-8">
                We regularly travel beyond Lancashire to surrounding counties. If your location isn't listed above, please get in touch – we'd be happy to discuss your requirements and provide a quote.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:07530166035"
                  className="px-8 py-4 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
                >
                  Call 07530 166035
                </a>
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
                >
                  Get a Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services CTA */}
      <section className="py-16 bg-charcoal text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h3 className="font-sans text-4xl font-bold mb-4">
              Our Services
            </h3>
            <p className="text-gray-300 text-lg">
              Available across all areas we cover
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/house-removals"
              className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-gold hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-home-4-line text-4xl text-gold mb-4"></i>
              <h4 className="font-sans text-xl font-bold mb-2">House Removals</h4>
              <p className="text-gray-300 text-sm">Complete home moving service</p>
            </Link>

            <Link
              to="/office-removals"
              className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-gold hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-building-line text-4xl text-gold mb-4"></i>
              <h4 className="font-sans text-xl font-bold mb-2">Office Removals</h4>
              <p className="text-gray-300 text-sm">Business relocations</p>
            </Link>

            <Link
              to="/single-item-moves"
              className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-gold hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-box-3-line text-4xl text-gold mb-4"></i>
              <h4 className="font-sans text-xl font-bold mb-2">Single Item Moves</h4>
              <p className="text-gray-300 text-sm">Furniture & appliances</p>
            </Link>

            <Link
              to="/collections-deliveries"
              className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-gold hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-truck-line text-4xl text-gold mb-4"></i>
              <h4 className="font-sans text-xl font-bold mb-2">Collections & Deliveries</h4>
              <p className="text-gray-300 text-sm">Pickup and delivery service</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AreasPage;
